/*!
 * @jscrypto/kdfs v0.9.1
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
import { concatBytes } from '@jscrypto/core';

const evpKdf = {
    kind: 'kdf',
    name: 'EvpKDF',
    derive(params, context) {
        const options = params;
        return deriveEvpKdf({
            ...options,
            hash: context.getHash(options.hash ?? 'MD5'),
        });
    },
};
const evpKdfPreset = {
    kind: 'preset',
    name: 'evpkdf',
    components() {
        return [evpKdf];
    },
};
function deriveEvpKdf(params) {
    assertPositiveInteger$1(params.length, 'EvpKDF length');
    if (params.iterations !== undefined) {
        assertPositiveInteger$1(params.iterations, 'EvpKDF iterations');
    }
    if (params.input === undefined || params.input === null) {
        throw new TypeError('EvpKDF requires input.');
    }
    if (params.salt === undefined || params.salt === null) {
        throw new TypeError('EvpKDF requires salt.');
    }
    const password = toBytes$2(params.input);
    const salt = toBytes$2(params.salt);
    const blocks = [];
    let previous = new Uint8Array(0);
    let length = 0;
    while (length < params.length) {
        let block = params.hash.hash(concatBytes(previous, password, salt));
        for (let i = 1; i < (params.iterations ?? 1); i++) {
            block = params.hash.hash(block);
        }
        blocks.push(block);
        previous = block;
        length += block.length;
    }
    return concatBytes(...blocks).slice(0, params.length);
}
function toBytes$2(input) {
    return typeof input === 'string' ? new TextEncoder().encode(input) : input;
}
function assertPositiveInteger$1(value, label) {
    if (!Number.isInteger(value) || value <= 0) {
        throw new RangeError(`${label} must be a positive integer.`);
    }
}

const hkdf = {
    kind: 'kdf',
    name: 'HKDF',
    derive(params, context) {
        const options = params;
        return deriveHkdf({
            ...options,
            hash: resolveHash(options.hash, context, 'HKDF'),
        });
    },
};
const hkdfExtract = {
    kind: 'kdf',
    name: 'HKDF-Extract',
    derive(params, context) {
        const options = params;
        return extractHkdf({
            input: options.input,
            salt: options.salt,
            hash: resolveHash(options.hash, context, 'HKDF-Extract'),
        });
    },
};
const hkdfExpand = {
    kind: 'kdf',
    name: 'HKDF-Expand',
    derive(params, context) {
        const options = params;
        return expandHkdf({
            input: options.input,
            info: options.info,
            length: options.length,
            hash: resolveHash(options.hash, context, 'HKDF-Expand'),
        });
    },
};
const hkdfPreset = {
    kind: 'preset',
    name: 'hkdf',
    components() {
        return [hkdf, hkdfExtract, hkdfExpand];
    },
};
function extractHkdf(params) {
    requireInput(params.input, 'HKDF');
    requireHash(params.hash, 'HKDF');
    if (params.salt !== undefined) {
        assertBytesOrString(params.salt, 'salt');
    }
    const hashLen = params.hash.digestSize;
    return extract(params.hash, toBytes$1(params.input), normalizeSalt(params.salt, hashLen));
}
function expandHkdf(params) {
    return expandFromParams(params, 'HKDF-Expand');
}
function deriveHkdf(params) {
    const prk = extractHkdf(params);
    return expandFromParams({
        input: prk,
        info: params.info,
        hash: params.hash,
        length: params.length,
    }, 'HKDF');
}
function resolveHash(hash, context, label) {
    if (typeof hash !== 'string' || hash.length === 0) {
        throw new TypeError(`${label} requires hash.`);
    }
    return context.getHash(hash);
}
function expandFromParams(params, label) {
    requireInput(params.input, label);
    requireHash(params.hash, label);
    assertNonNegativeInteger(params.length, `${label} length`);
    const hashLen = params.hash.digestSize;
    const prk = toBytes$1(params.input);
    if (prk.length < hashLen) {
        throw new RangeError(`${label} input must be at least ${hashLen} bytes.`);
    }
    const maxLength = 255 * hashLen;
    if (params.length > maxLength) {
        throw new RangeError(`${label} length must be <= ${maxLength}.`);
    }
    if (params.info !== undefined) {
        assertBytesOrString(params.info, 'info');
    }
    if (params.length === 0) {
        return new Uint8Array(0);
    }
    const info = params.info === undefined ? new Uint8Array(0) : toBytes$1(params.info);
    return expand(params.hash, prk, info, params.length);
}
function extract(hash, input, salt) {
    return hmac$1(hash, salt, input);
}
function expand(hash, prk, info, length) {
    const hashLen = hash.digestSize;
    const blockCount = Math.ceil(length / hashLen);
    const blocks = [];
    let previous = new Uint8Array(0);
    for (let index = 1; index <= blockCount; index++) {
        previous = hmac$1(hash, prk, concatBytes(previous, info, new Uint8Array([index])));
        blocks.push(previous);
    }
    return concatBytes(...blocks).slice(0, length);
}
function hmac$1(hash, key, input) {
    const normalizedKey = key.length > hash.blockSize ? hash.hash(key) : key;
    const innerKey = new Uint8Array(hash.blockSize);
    const outerKey = new Uint8Array(hash.blockSize);
    innerKey.set(normalizedKey);
    outerKey.set(normalizedKey);
    for (let i = 0; i < hash.blockSize; i++) {
        innerKey[i] = innerKey[i] ^ 0x36;
        outerKey[i] = outerKey[i] ^ 0x5c;
    }
    return hash.hash(concatBytes(outerKey, hash.hash(concatBytes(innerKey, input))));
}
function normalizeSalt(salt, hashLen) {
    if (salt === undefined) {
        return new Uint8Array(hashLen);
    }
    const bytes = toBytes$1(salt);
    return bytes.length === 0 ? new Uint8Array(hashLen) : bytes;
}
function toBytes$1(input) {
    return typeof input === 'string' ? new TextEncoder().encode(input) : input;
}
function requireInput(value, label) {
    if (value === undefined || value === null) {
        throw new TypeError(`${label} requires input.`);
    }
    assertBytesOrString(value, 'input');
}
function requireHash(value, label) {
    if (value === undefined || value === null) {
        throw new TypeError(`${label} requires hash.`);
    }
}
function assertBytesOrString(value, name) {
    if (typeof value === 'string') {
        return;
    }
    if (value instanceof Uint8Array) {
        return;
    }
    throw new TypeError(`HKDF ${name} must be a Uint8Array or string.`);
}
function assertNonNegativeInteger(value, label) {
    if (!Number.isInteger(value) || value < 0) {
        throw new RangeError(`${label} must be a non-negative integer.`);
    }
}

function hmac(hash, key, input) {
    const normalizedKey = key.length > hash.blockSize ? hash.hash(key) : key;
    const innerKey = new Uint8Array(hash.blockSize);
    const outerKey = new Uint8Array(hash.blockSize);
    innerKey.set(normalizedKey);
    outerKey.set(normalizedKey);
    for (let i = 0; i < hash.blockSize; i++) {
        innerKey[i] ^= 0x36;
        outerKey[i] ^= 0x5c;
    }
    return hash.hash(concatBytes(outerKey, hash.hash(concatBytes(innerKey, input))));
}

const pbkdf2 = {
    kind: 'kdf',
    name: 'PBKDF2',
    derive(params, context) {
        const options = params;
        return derivePbkdf2({
            ...options,
            hash: context.getHash(options.hash ?? 'SHA256'),
        });
    },
};
const pbkdf2Preset = {
    kind: 'preset',
    name: 'pbkdf2',
    components() {
        return [pbkdf2];
    },
};
function derivePbkdf2(params) {
    assertPositiveInteger(params.iterations, 'PBKDF2 iterations');
    assertPositiveInteger(params.length, 'PBKDF2 length');
    if (params.input === undefined || params.input === null) {
        throw new TypeError('PBKDF2 requires input.');
    }
    if (params.salt === undefined || params.salt === null) {
        throw new TypeError('PBKDF2 requires salt.');
    }
    const password = toBytes(params.input);
    const salt = toBytes(params.salt);
    const blocks = [];
    const blockCount = Math.ceil(params.length / params.hash.digestSize);
    for (let index = 1; index <= blockCount; index++) {
        const counter = new Uint8Array([
            (index >>> 24) & 0xff,
            (index >>> 16) & 0xff,
            (index >>> 8) & 0xff,
            index & 0xff,
        ]);
        let block = hmac(params.hash, password, concatBytes(salt, counter));
        const result = block.slice();
        for (let iteration = 1; iteration < params.iterations; iteration++) {
            block = hmac(params.hash, password, block);
            for (let i = 0; i < result.length; i++) {
                result[i] ^= block[i];
            }
        }
        blocks.push(result);
    }
    return concatBytes(...blocks).slice(0, params.length);
}
function toBytes(input) {
    return typeof input === 'string' ? new TextEncoder().encode(input) : input;
}
function assertPositiveInteger(value, label) {
    if (!Number.isInteger(value) || value <= 0) {
        throw new RangeError(`${label} must be a positive integer.`);
    }
}

function classicKdfsPreset() {
    return {
        kind: 'preset',
        name: 'classic-kdfs',
        components() {
            return [pbkdf2Preset, evpKdfPreset];
        },
    };
}
function kdfsPreset() {
    return {
        kind: 'preset',
        name: 'kdfs',
        components() {
            return [classicKdfsPreset(), hkdfPreset];
        },
    };
}

export { classicKdfsPreset, deriveEvpKdf, deriveHkdf, derivePbkdf2, evpKdf, evpKdfPreset, expandHkdf, extractHkdf, hkdf, hkdfExpand, hkdfExtract, hkdfPreset, hmac, kdfsPreset, pbkdf2, pbkdf2Preset };
//# sourceMappingURL=index.mjs.map
